/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { QUERY_PERSIST_CACHE, useAppConfig } from '@airasia-rn/ui';
import { RECAPTCHA_ACTIONS } from '../constants/recaptcha';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  QueryClient,
  queryOptions,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { NativeModules, Platform } from 'react-native';
import {
  createFacebookSignInManager,
  createGoogleSignInManager,
  createRecaptchaManager,
} from '../managers';
import { AccountConfigContext } from '../config/AccountConfigProvider';
import {
  guestLoginKey,
  handleGuestLogin,
  useGuestLogin,
} from './useGuestLogin';
import {
  getUserId as getUserIdFromToken,
  hasJwtExpired,
} from '../utils/jwtUtils';
import { removeCookie, setCookie } from '../utils/cookies';
import { useSchemaReporter } from './useSchemaReporter';
import { interpretGoogleAuthExchange } from '../utils/googleAuthExchange';
import {
  appendExternalAuth,
  isTrustedAuthHost,
  type ExternalAuthTokens,
  type NearbyLocation,
} from '../utils/constructSSOAuth';
import {
  AccessTokenResponse,
  AccountDeletionResponse,
  ChangeUserNameRequest,
  ClientKeyResponse,
  SsoApiService,
  SecurityActivityResponse,
  TravelDocResponse,
  UpdateTravelDocRequest,
  User,
  UserUpdateRequestBody,
} from '../managers/sso';
import { LoggerProviderType, useLogger } from '@airasia-rn/core-logger';

import { describeError } from '../utils/describeError';
/**
 * Provides token validity check and lazy token retrieval.
 *
 * - `isTokenValid`: boolean evaluated at render time — use in `enabled` guards
 *    and other synchronous/render-time checks.
 * - `getToken`: async getter that returns the cached token if valid, otherwise
 *    triggers a refetch. Uses the `isValid` closure internally for a fresh
 *    check at call time, ensuring the value is never stale in async contexts.
 */
const tokenAccessor = <T>(
  query: { data: T; refetch: () => Promise<{ data: T }> },
  isValid: () => boolean,
) => ({
  isTokenValid: isValid(),
  getToken: async () => {
    if (!isValid()) {
      const result = await query.refetch();
      return result.data;
    }
    return query.data;
  },
});

export const FORCE_LOGOUT_ERRORS = [
  'INVALID_REFRESH_TOKEN',
  'USER_LOCKED',
  'USER_DELETED',
  'USER_TERMINATED',
  'USER_NOT_ACTIVATED',
  'USER_SCHEDULED_FOR_DELETION',
] as const;

const TOKEN_REFRESH_BUFFER_SECONDS = 5 * 60;

export const accountKeys = {
  apiKey: [QUERY_PERSIST_CACHE, 'sso_api_key'],
  refreshToken: ['sso', 'refresh_token'],
  accessToken: [QUERY_PERSIST_CACHE, 'sso', 'access_token'],
  idToken: [QUERY_PERSIST_CACHE, 'sso', 'id_token'],
  userId: [QUERY_PERSIST_CACHE, 'sso', 'user_id'],
  user: [QUERY_PERSIST_CACHE, 'sso', 'user'],
  isGuestLogin: guestLoginKey,
  // Not persisted: the response carries passport number, MRZ code and national
  // identity number, which must not sit in browser storage. Matches savedCards.
  travelDoc: ['sso', 'travel_doc'],
  notificationPreferences: [
    QUERY_PERSIST_CACHE,
    'sso',
    'notification_preferences',
  ],
  notificationPreferencesConfig: ['sso', 'notification_preferences_config'],
  lifeProfile: [QUERY_PERSIST_CACHE, 'sso', 'life_profile'],
  savedCards: ['sso', 'saved_cards'],
  mobileVerificationToken: ['sso_mobile_verification_token'],
  twoFaLoginToken: ['sso_two_fa_login_token'],
  userDetailChangeConfiguration: [
    QUERY_PERSIST_CACHE,
    'sso',
    'user_detail_change_configuration',
  ],
  securityActivity: [QUERY_PERSIST_CACHE, 'sso', 'security_activity'],
  twoFaActivity: [QUERY_PERSIST_CACHE, 'sso', 'two_fa_activity'],
  recentSecurityActivityDetail: (sessionId: string) =>
    [
      QUERY_PERSIST_CACHE,
      'sso',
      'recent_security_activity_detail',
      sessionId,
    ] as const,
  favouriteMembers: [QUERY_PERSIST_CACHE, 'sso', 'favourite_members'],
  favouritedItems: [QUERY_PERSIST_CACHE, 'sso', 'favourited_items'],
  registeredPasskeys: [QUERY_PERSIST_CACHE, 'sso', 'registered_passkeys'],
  mergeEligibility: [QUERY_PERSIST_CACHE, 'sso', 'merge_eligibility'],
  creditAccount: ['sso', 'credit_account'],
  creditBalance: [QUERY_PERSIST_CACHE, 'sso', 'credit_balance'],
  externalAuthUrl: (url: string) => ['sso', 'external_auth_url', url] as const,
  // Transient one-shot signal from Personal/Travel Details -> Manage Account.
  detailsUpdateSnackbar: ['sso', 'details_update_snackbar'],
};

export const accountStorageKeys = {
  combineAccountsContinueTapped: 'combine_accounts_continue_tapped',
  combineAccountsRemindLaterTapped: 'combine_accounts_remind_later_tapped',
  passkeyBottomSheetTime: (userId: string) =>
    `passkey_bottom_sheet_time_${userId}`,
  debugMode: 'debug_mode',
};

/**
 * Clears local auth state and hands off to the native force-logout flow.
 *
 * Single routine for every client-side dead-session scenario so they all sign
 * the user out identically:
 * - a force-logout error code from the server ({@link handleApiError}),
 * - a missing refresh token ({@link getRefreshToken}), and
 * - an expired refresh token ({@link useAccessToken}).
 *
 * When a local-logout mutation is supplied (hook context) it is used for the
 * fullest cleanup; otherwise the persisted auth keys are removed directly
 * (imperative/standalone context). The final step hands off to the native
 * "session expired" flow, optional-chained so it no-ops where the bridge is
 * absent (e.g. web).
 *
 * @param queryClient - Optional query client for cache clearing.
 * @param logoutMutation - Optional local-logout mutation for the fuller cleanup.
 */
const performForceLogout = async (
  queryClient?: QueryClient,
  logoutMutation?: ReturnType<typeof useLocalLogout>,
): Promise<void> => {
  if (logoutMutation) {
    await logoutMutation.mutateAsync();
  } else {
    await AsyncStorage.multiRemove([
      accountKeys.accessToken.slice(1).join('_'),
      accountKeys.refreshToken.join('_'),
      accountKeys.userId.slice(1).join('_'),
      accountKeys.user.slice(1).join('_'),
      accountKeys.twoFaLoginToken[0],
      'user_id',
      accountStorageKeys.combineAccountsContinueTapped,
      accountStorageKeys.combineAccountsRemindLaterTapped,
    ]);
  }

  if (queryClient) {
    queryClient.removeQueries({ queryKey: accountKeys.refreshToken });
    queryClient.removeQueries({ queryKey: accountKeys.accessToken });
    queryClient.removeQueries({ queryKey: accountKeys.idToken });
    queryClient.removeQueries({ queryKey: accountKeys.user });
    queryClient.removeQueries({ queryKey: accountKeys.travelDoc });
    queryClient.removeQueries({ queryKey: accountKeys.creditBalance });
    queryClient.removeQueries({ queryKey: accountKeys.lifeProfile });
    queryClient.removeQueries({ queryKey: accountKeys.savedCards });
    queryClient.removeQueries({ queryKey: accountKeys.creditAccount });
    queryClient.removeQueries({ queryKey: accountKeys.userId });
    queryClient.removeQueries({
      queryKey: accountKeys.userDetailChangeConfiguration,
    });
  }

  // Hand off to the host app's native bridge where it exists; where it's absent
  // (move-app, web) the shell reacts to the cleared auth state above instead.
  const nativeForceLogout = NativeModules?.NativeSSO?.forceLogout;
  if (typeof nativeForceLogout === 'function') {
    try {
      nativeForceLogout();
    } catch (nativeLogoutError) {
      console.warn('Native force logout bridge failed:', nativeLogoutError);
    }
  }
};

/**
 * Centralized API error handler that automatically handles force logout scenarios
 * This function checks for force logout errors and triggers the logout process
 *
 * @param error - The error that occurred during API call
 * @param logoutMutation - The logout mutation function
 * @param queryClient - Optional query client for cache clearing
 * @returns Promise<boolean> - Returns true if force logout was handled, false otherwise
 */
export const handleApiError = async (
  error: unknown,
  logoutMutation?: ReturnType<typeof useLocalLogout>,
  queryClient?: QueryClient,
): Promise<boolean> => {
  // Check for force logout error codes
  if (axios.isAxiosError(error)) {
    const errorCode = error.response?.data?.code;
    if (errorCode && FORCE_LOGOUT_ERRORS.includes(errorCode)) {
      console.warn('Force logout triggered due to error:', errorCode);

      // Shared dead-session cleanup: local logout (mutation when provided, else
      // direct key removal), React Query cache clearing, and the native
      // force-logout hand-off — so a force-logout detected here from any LOB
      // (home, flights, hotels, …) behaves exactly like the client-side paths.
      await performForceLogout(queryClient, logoutMutation);

      return true; // Indicate that force logout was handled
    }
  }

  return false;
};

/**
 * Enhanced useQuery hook that automatically handles force logout errors
 * This wrapper provides the same API as useQuery but with automatic force logout handling
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { data, isLoading, error } = useApiQuery({
 *     queryKey: ['user'],
 *     queryFn: () => api.getUser(),
 *   });
 *
 *   // Force logout errors are handled automatically
 *   // No need for manual try-catch blocks
 * };
 * ```
 */
export const useApiQuery = <TQueryFnData, TError, TData = TQueryFnData>(
  options: UseQueryOptions<TQueryFnData, TError, TData>,
) => {
  const queryClient = useQueryClient();
  const localLogout = useLocalLogout();
  // Lets the API services report schema drift through this app's logger.
  useSchemaReporter();

  // Bug fix: useLocalLogout returns a new object every render (useMutation behaviour).
  // Storing it in a ref means wrappedQueryFn can always call the latest logout
  // without being recreated on every render, which previously caused React Query
  // to re-register the observer and trigger an infinite re-render loop.
  const localLogoutRef = useRef(localLogout);
  localLogoutRef.current = localLogout;

  const wrappedQueryFn = useCallback(async () => {
    try {
      // @ts-expect-error - We know queryFn exists from UseQueryOptions
      return await options.queryFn();
    } catch (error) {
      // Handle force logout errors automatically
      const wasForceLogout = await handleApiError(
        error,
        localLogoutRef.current,
        queryClient,
      );

      if (wasForceLogout) {
        // If it was a force logout, we can either re-throw or return a default value
        // For now, we'll re-throw to maintain existing behavior
        throw error;
      }

      // If it wasn't a force logout, re-throw the original error
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.queryFn, queryClient]);

  return useQuery({
    ...options,
    queryFn: wrappedQueryFn,
  });
};

/**
 * Enhanced useMutation hook that automatically handles force logout errors
 * This wrapper provides the same API as useMutation but with automatic force logout handling
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const updateUserMutation = useApiMutation({
 *     mutationFn: (data) => api.updateUser(data),
 *     onSuccess: (data) => {
 *       console.log('User updated:', data);
 *     },
 *   });
 *
 *   // Force logout errors are handled automatically
 *   // No need for manual try-catch blocks in onError
 * };
 * ```
 */
export const useApiMutation = <TData, TError, TVariables, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const queryClient = useQueryClient();
  const localLogout = useLocalLogout();
  // Lets the API services report schema drift through this app's logger.
  useSchemaReporter();

  const wrappedMutationFn = async (variables: TVariables) => {
    try {
      return await options.mutationFn!(variables);
    } catch (error) {
      // Handle force logout errors automatically
      const wasForceLogout = await handleApiError(
        error,
        localLogout,
        queryClient,
      );

      if (wasForceLogout) {
        // If it was a force logout, we can either re-throw or return a default value
        // For now, we'll re-throw to maintain existing behavior
        throw error;
      }

      // If it wasn't a force logout, re-throw the original error
      throw error;
    }
  };

  return useMutation({
    ...options,
    mutationFn: wrappedMutationFn,
  });
};

export class RefreshTokenExpiredError extends Error {
  constructor() {
    super('Refresh token expired');
    this.name = 'RefreshTokenExpiredError';
  }
}

/**
 * React hook that provides a memoized instance of {@link SsoApiService}.
 *
 * - Uses `useAppConfig` to determine environment values.
 * - On non-web platforms, it binds the API base URL to `https://mobile.airasia.com`.
 * - On web, uses an empty string (can be proxied or overridden).
 *
 * @returns {SsoApiService} Singleton-like instance reused across renders.
 */
export function useSsoApiService(): SsoApiService {
  const appConfig = useAppConfig();
  return useMemo(() => {
    return new SsoApiService(
      Platform.OS !== 'web' ? 'https://mobile.airasia.com' : '',
      appConfig.config.env,
      appConfig.config.dvToken,
    );
  }, [appConfig.config.env, appConfig.config.dvToken]);
}

/**
 * React hook that provides a memoized recaptcha manager instance.
 *
 * Reads the site key from {@link AccountConfigProvider} and uses
 * {@link createRecaptchaManager} following the same pattern as passkey manager.
 */
export function useRecaptchaManager() {
  const ctx = useContext(AccountConfigContext);
  const siteKey = ctx?.config.recaptchaSiteKey ?? '';

  if (!siteKey) {
    console.warn(
      'useRecaptchaManager: recaptchaSiteKey is missing from AccountConfigContext. reCAPTCHA will not function correctly.',
    );
  }

  return useMemo(() => createRecaptchaManager(siteKey), [siteKey]);
}

export function useGoogleSignInManager() {
  return useMemo(() => createGoogleSignInManager(), []);
}

export function useFacebookSignInManager() {
  return useMemo(() => createFacebookSignInManager(), []);
}

/**
 * React Query options for fetching the refresh token from AsyncStorage.
 *
 * - Reads the locally persisted refresh token.
 * - Does not make any network calls.
 * - Resolves to `''` when no token is stored. A missing token is a normal
 *   signed-out state, not an error, so the query succeeds with an empty string
 *   rather than rejecting; only a genuine AsyncStorage read failure rejects.
 *
 * @returns React Query options object.
 */
const refreshTokenQueryOptions = (caller?: string) =>
  queryOptions<string>({
    queryKey: accountKeys.refreshToken,
    retry: false,
    queryFn: async () => {
      const token = await AsyncStorage.getItem(
        accountKeys.refreshToken.join('_'),
      );
      if (!token) {
        // Logged-out is a valid state, not an error. Resolve to '' so both
        // getters honour their `Promise<string>` contract and consumers can
        // rely on truthiness/`.length` checks. `caller` is retained for
        // debugging where a token was expected but absent.
        console.debug(`${caller ?? 'account'}: No refresh token found`);
        return '';
      }
      return token;
    },
  });

/**
 * Hook that subscribes to the refresh token value stored in AsyncStorage.
 *
 * - Automatically keeps components updated with changes to refresh token state.
 * - `data` is the token string, or `''` when logged out. A missing token does
 *   not put the query into an error state — it succeeds with an empty string.
 */
export const useRefreshToken = (caller?: string) => {
  return useQuery<string>(refreshTokenQueryOptions(caller));
};

/**
 * Imperative function to retrieve the refresh token from the cache.
 *
 * - Uses `queryClient.ensureQueryData` to load the locally persisted token.
 * - Since the refresh token is local-only, this never hits the network.
 * - Resolves to `''` when no token is stored. On a real (non-guest) session a
 *   missing token means the session is gone, so this force-logs the user out
 *   (clears auth + hands off to the native "session expired" flow) before
 *   returning `''`. Guest sessions legitimately have no refresh token and are
 *   exempt.
 * - Only a genuine AsyncStorage read failure rejects; a storage error is NOT
 *   treated as a missing token, so a transient glitch never force-logs-out a
 *   valid user.
 *
 * @param queryClient Instance of QueryClient
 * @returns {Promise<string>} Refresh token string, or `''` if none is stored.
 */
export const getRefreshToken = async (
  queryClient: QueryClient,
): Promise<string> => {
  // refreshTokenQueryOptions resolves to '' for a missing token and only
  // rejects on a real storage failure, so '' reliably means "no session" here
  // without us having to swallow IO errors.
  const token = await queryClient.ensureQueryData(refreshTokenQueryOptions());

  if (!token) {
    // No refresh token: the session is gone. Force logout so the user is taken
    // through the native sign-in / "session expired" flow — unless this is a
    // guest session, where an absent refresh token is expected.
    const isGuest =
      (await AsyncStorage.getItem(accountKeys.isGuestLogin.join('_'))) ===
      'true';
    if (!isGuest) {
      await performForceLogout(queryClient);
    }
    return '';
  }

  return token;
};

/**
 * Hook that retrieves the user ID from the refresh token.
 *
 * - Decodes the refresh token to extract the user ID.
 * - Enabled only when a refresh token is available.
 */
export const useUserId = (caller?: string) => {
  const refreshTokenQuery = useRefreshToken(caller);

  return useQuery<string>({
    queryKey: accountKeys.userId,
    queryFn: () => getUserIdFromToken(refreshTokenQuery.data),
    enabled: !!refreshTokenQuery.data,
  });
};

/**
 * Imperative function to retrieve the user ID.
 *
 * - Fetches the refresh token and then uses it to get the user ID.
 *
 * @param queryClient Instance of QueryClient
 * @returns {Promise<string>} User ID string.
 */
export const getUserId = async (queryClient: QueryClient): Promise<string> => {
  const refreshToken = await getRefreshToken(queryClient);
  return getUserIdFromToken(refreshToken);
};

/**
 * React Query options for fetching an API key from the SSO service.
 *
 * - Calls the SSO `/clientKey` API.
 * - Cached for 59 minutes to minimize duplicate network calls.
 *
 * @param api Instance of {@link SsoApiService}
 */
export const apiKeyQueryOptions = (
  api: ReturnType<typeof useSsoApiService>,
  logError?: (
    error: unknown,
    context?: Record<string, any>,
    type?: LoggerProviderType | LoggerProviderType[],
  ) => void,
) =>
  queryOptions<ClientKeyResponse>({
    queryKey: accountKeys.apiKey,
    staleTime: 59 * 60 * 1000, // 59 mins
    queryFn: async () => {
      try {
        return await api.getClientKey();
      } catch (error) {
        logError?.(error, { context: 'Error fetching API key:' });
        throw error;
      }
    },
  });

// Reuse cached client key when still valid; otherwise fetch once.
export const ensureSocialAuthClientKey = async (
  queryClient: QueryClient,
  api: ReturnType<typeof useSsoApiService>,
): Promise<ClientKeyResponse> => {
  const cached = queryClient.getQueryData<ClientKeyResponse>(
    accountKeys.apiKey,
  );
  const hasValidKey =
    Boolean(cached?.id) &&
    Boolean(cached?.apiKey) &&
    !hasJwtExpired(cached!.apiKey!, TOKEN_REFRESH_BUFFER_SECONDS);

  if (hasValidKey && cached) {
    return cached;
  }

  return queryClient.fetchQuery(apiKeyQueryOptions(api));
};

/**
 * Hook that fetches and caches an API key from the SSO service.
 *
 * - Automatically handles caching with a 59-minute stale time.
 * - Returns apiKey and clientId (id) for API authentication
 */
export const useApiKey = () => {
  const api = useSsoApiService();
  const { logError } = useLogger();
  const query = useQuery(apiKeyQueryOptions(api, logError));

  const isValidToken = () => {
    const apikey = query.data?.apiKey;
    return !!apikey && !hasJwtExpired(apikey, TOKEN_REFRESH_BUFFER_SECONDS);
  };

  return {
    ...query,
    ...tokenAccessor(query, isValidToken),
  };
};

/**
 * Hook that retrieves a valid access token for authenticated API calls.
 *
 * - Transparently handles token refresh using the refresh token.
 * - Will return cached token if still valid.
 * - Throws normalized `"SessionExpired"` error when refresh fails.
 */
export const useAccessToken = (caller: string) => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const logoutMutation = useLocalLogout();
  const apiKeyQuery = useApiKey();
  const isLoggedInQuery = useIsLoggedIn();

  // Hold a stable ref to logoutMutation so the queryFn closure doesn't
  // need to be recreated when the mutation object reference changes.
  const logoutMutationRef = useRef(logoutMutation);
  logoutMutationRef.current = logoutMutation;

  const { logError } = useLogger();

  const query = useApiQuery<string, Error>({
    queryKey: accountKeys.accessToken,
    staleTime: 9 * 60 * 1000, // 9 mins, make it less if cache was only in-memory
    queryFn: async () => {
      try {
        const refreshToken = await getRefreshToken(queryClient);

        // A missing token is already fully handled inside getRefreshToken (it
        // force-logs-out a real session and exempts guests). Abort here without
        // logging out again — otherwise the expired branch below would fire the
        // native force-logout bridge a second time.
        if (!refreshToken) {
          throw new RefreshTokenExpiredError();
        }

        if (hasJwtExpired(refreshToken)) {
          // A present-but-expired refresh token is a dead session, same as a
          // missing one — run the same shared force logout so both paths behave
          // identically and match the server-side INVALID_REFRESH_TOKEN handling
          // in handleApiError. Pass the local-logout mutation for fuller cleanup.
          await performForceLogout(queryClient, logoutMutationRef.current);
          throw new RefreshTokenExpiredError();
        }

        const token =
          (
            await api.refreshToken(
              apiKeyQuery.data?.id || 'NA',
              refreshToken || 'NA',
              apiKeyQuery.data?.apiKey || 'NA',
            )
          ).accessToken || '';
        return token || '';
      } catch (error) {
        logError(error, { context: 'Error fetching access token:', caller });
        throw error;
      }
    },
    enabled: apiKeyQuery.isTokenValid && isLoggedInQuery.isLoggedIn,
  });

  const isValidToken = () => {
    const currentToken = query.data;
    return (
      isLoggedInQuery.isSuccess &&
      isLoggedInQuery.isLoggedIn &&
      !!currentToken &&
      !hasJwtExpired(currentToken, TOKEN_REFRESH_BUFFER_SECONDS)
    );
  };

  return {
    ...query,
    ...tokenAccessor(query, isValidToken),
  };
};

/**
 * Hook that retrieves and caches a valid ID token for the logged-in user.
 */
export const useIdToken = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const apiKeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken(caller);

  const query = useApiQuery<string, Error>({
    queryKey: accountKeys.idToken,
    staleTime: 9 * 60 * 1000,
    queryFn: async () => {
      const [apiKey, accessToken] = await Promise.all([
        apiKeyQuery.getToken(),
        accessTokenQuery.getToken(),
      ]);

      if (!apiKey?.apiKey) throw new Error('Missing API key');
      if (!apiKey.id) throw new Error('Missing Client ID');
      if (!userIdQuery.data) throw new Error('Missing user ID');
      if (!accessToken) throw new Error('Missing access token');

      const { idToken } = await api.getIdToken(
        apiKey.apiKey,
        apiKey.id,
        userIdQuery.data,
        accessToken,
      );
      return idToken || '';
    },
    enabled:
      !!userIdQuery.data &&
      apiKeyQuery.isTokenValid &&
      accessTokenQuery.isTokenValid,
  });

  const isValidToken = () =>
    !!query.data && !hasJwtExpired(query.data, TOKEN_REFRESH_BUFFER_SECONDS);

  return {
    ...query,
    ...tokenAccessor(query, isValidToken),
  };
};

/**
 * Hook that exposes login state.
 *
 * - Derives from {@link useRefreshToken}.
 * - A user is considered logged in if the refresh token exists and has length > 0.
 */
export const useIsLoggedIn = (caller?: string) => {
  const query = useRefreshToken(caller);
  const guestLoginQuery = useGuestLogin();

  // Bug fix: the previous `{ ...query, isLoggedIn, isGuestUser }` spread exposed
  // ALL React Query fields (isFetching, fetchStatus, status, dataUpdatedAt, …).
  // Every background refetch of the refresh token caused every consumer
  // (useFetchSettings, useAccessToken, etc.) to re-render, even when none of
  // the values they actually use had changed.
  // Now we return only the fields that consumers need, memoised so the object
  // reference is stable when none of the values change.
  return useMemo(
    () => ({
      isLoggedIn: (query.data ?? '').length > 0,
      isGuestUser: guestLoginQuery.data ?? false,
      isSuccess: query.isSuccess ?? false,
      isLoading: query.isLoading ?? false,
      isPending: query.isPending ?? false,
      isFetched: query.isFetched ?? false,
      isError: query.isError ?? false,
      error: query.error ?? null,
    }),
    [
      query.data,
      query.isSuccess,
      query.isLoading,
      query.isPending,
      query.isFetched,
      query.isError,
      query.error,
      guestLoginQuery.data,
    ],
  );
};

/**
 * Hook that retrieves full user profile information.
 *
 * - Requires that user ID, API key, and access token are all available.
 * - Disabled until all prerequisites are satisfied.
 * - Always refetches in the background on mount; cached data is shown instantly while fresh data loads.
 */
export const useUser = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const apikeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken('userUser');

  return useApiQuery<User, Error>({
    queryKey: accountKeys.user,
    staleTime: 0, // always stale: serve cache instantly, refetch in background on every mount
    queryFn: async () => {
      const [apiKey, accessToken] = await Promise.all([
        apikeyQuery.getToken(),
        accessTokenQuery.getToken(),
      ]);

      if (!apiKey?.apiKey) throw new Error('Missing API key');
      if (!apiKey?.id) throw new Error('Missing Client ID');
      if (!userIdQuery.data) throw new Error('Missing user ID');
      if (!accessToken) throw new Error('Missing access token');

      const userData = await api.getUser(
        apiKey.apiKey,
        apiKey.id,
        userIdQuery.data,
        accessToken,
      );
      return userData;
    },
    enabled:
      (userIdQuery.data?.length || 0) > 0 &&
      apikeyQuery.isTokenValid &&
      accessTokenQuery.isTokenValid,
  });
};

export const use2FAActivity = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const apikeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken(caller);

  return useApiQuery<SecurityActivityResponse, Error>({
    queryKey: accountKeys.twoFaActivity,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [apiKey, accessToken] = await Promise.all([
        apikeyQuery.getToken(),
        accessTokenQuery.getToken(),
      ]);

      if (!apiKey?.apiKey) throw new Error('Missing API key');
      if (!apiKey?.id) throw new Error('Missing Client ID');
      if (!userIdQuery.data) throw new Error('Missing user ID');
      if (!accessToken) throw new Error('Missing access token');

      const response = await api.getSecurityActivity(
        apiKey.apiKey,
        apiKey.id,
        userIdQuery.data,
        accessToken,
        { limit: 10, type: 'two_factor_auth_preferences_change' },
      );
      return response;
    },
    enabled:
      (userIdQuery.data?.length || 0) > 0 &&
      apikeyQuery.isTokenValid &&
      accessTokenQuery.isTokenValid,
  });
};

export const useTravelDoc = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const apikeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken('userUser');

  return useApiQuery<TravelDocResponse, Error>({
    queryKey: accountKeys.travelDoc,
    staleTime: 0, // always stale: serve cache instantly, refetch in background on every mount
    queryFn: async () => {
      try {
        const [apiKey, accessToken] = await Promise.all([
          apikeyQuery.getToken(),
          accessTokenQuery.getToken(),
        ]);

        if (!apiKey?.apiKey) throw new Error('Missing API key');
        if (!apiKey?.id) throw new Error('Missing Client ID');
        if (!userIdQuery.data) throw new Error('Missing user ID');
        if (!accessToken) throw new Error('Missing access token');

        return await api.getTravelDoc(
          apiKey.apiKey,
          apiKey.id,
          userIdQuery.data,
          accessToken,
        );
      } catch (error) {
        // Handle 404 errors gracefully - user may not have a travel doc yet
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          // Return empty travel doc response instead of throwing error
          return {} as TravelDocResponse;
        }
        // Re-throw other errors
        throw error;
      }
    },
    enabled:
      (userIdQuery.data?.length || 0) > 0 &&
      apikeyQuery.isTokenValid &&
      accessTokenQuery.isTokenValid,
  });
};

/**
 * Calls `DELETE /um/v2/users/{userId}` to initiate account deletion, then the app continues
 * with the email OTP verification step (delete-account flow).
 */
export const useInitiateAccountDeletion = (caller: string) => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const userIdQuery = useUserId(caller);
  const apikeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken(caller);

  return useApiMutation<AccountDeletionResponse, Error, void>({
    mutationFn: async () => {
      const [apiKey, accessToken, refreshToken] = await Promise.all([
        apikeyQuery.getToken(),
        accessTokenQuery.getToken(),
        getRefreshToken(queryClient),
      ]);

      if (!apiKey?.apiKey) throw new Error('Missing API key');
      if (!apiKey?.id) throw new Error('Missing Client ID');
      if (!userIdQuery.data) throw new Error('Missing user ID');
      if (!accessToken) throw new Error('Missing access token');
      if (!refreshToken) throw new Error('Missing refresh token');

      return api.deleteAccount(
        userIdQuery.data,
        apiKey.apiKey,
        accessToken,
        apiKey.id,
        refreshToken,
      );
    },
    onError: (error) => {
      console.error('Error initiating account deletion:', describeError(error));
    },
  });
};

/**
 * Hook for updating user profile information.
 *
 * - Uses SSO API service to update user data.
 * - Invalidates user query cache on success to trigger refetch.
 * - Handles force logout scenarios automatically via useApiMutation wrapper.
 *
 * @returns {UseMutationResult<User, Error, UserUpdateRequestBody>} React Query mutation for updating user.
 */
export const useUpdateUser = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const apikeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken('userUser');
  const queryClient = useQueryClient();

  return useApiMutation<User, Error, UserUpdateRequestBody>({
    mutationFn: async (userUpdateRequestBody: UserUpdateRequestBody) => {
      const [apiKey, accessToken] = await Promise.all([
        apikeyQuery.getToken(),
        accessTokenQuery.getToken(),
      ]);

      if (!apiKey?.apiKey) throw new Error('Missing API key');
      if (!apiKey?.id) throw new Error('Missing Client ID');
      if (!userIdQuery.data) throw new Error('Missing user ID');
      if (!accessToken) throw new Error('Missing access token');

      return await api.updateUser(
        apiKey.apiKey,
        apiKey.id,
        userIdQuery.data,
        accessToken,
        userUpdateRequestBody,
      );
    },
    onSuccess: (data) => {
      // Update user cache with new data
      queryClient.setQueryData(accountKeys.user, data);
      // Invalidate user query to ensure fresh data
      queryClient.invalidateQueries({ queryKey: accountKeys.user });
      // The profile card (MainAccountView) renders the name from the Life
      // profile, not the SSO user. Invalidate it so a name change here is
      // reflected there instead of showing stale data until the cache expires.
      queryClient.invalidateQueries({ queryKey: accountKeys.lifeProfile });
    },
    onError: (error) => {
      // Force logout scenarios are handled automatically by useApiMutation
      console.error('Error updating user:', describeError(error));
    },
  });
};

/**
 * Hook for updating travel document information.
 *
 * - Uses SSO API service to update travel document data.
 * - Invalidates travel doc query cache on success to trigger refetch.
 * - Handles force logout scenarios automatically via useApiMutation wrapper.
 *
 * @returns {UseMutationResult<TravelDocResponse, Error, UpdateTravelDocRequest>} React Query mutation for updating travel doc.
 */
export const useUpdateTravelDoc = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const apikeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken('userUser');
  const queryClient = useQueryClient();

  return useApiMutation<TravelDocResponse, Error, UpdateTravelDocRequest>({
    mutationFn: async (updateTravelDocRequest: UpdateTravelDocRequest) => {
      const [apiKey, accessToken] = await Promise.all([
        apikeyQuery.getToken(),
        accessTokenQuery.getToken(),
      ]);

      if (!apiKey?.apiKey) throw new Error('Missing API key');
      if (!apiKey?.id) throw new Error('Missing Client ID');
      if (!userIdQuery.data) throw new Error('Missing user ID');
      if (!accessToken) throw new Error('Missing access token');

      return await api.updateTravelDoc(
        apiKey.apiKey,
        apiKey.id,
        userIdQuery.data,
        accessToken,
        updateTravelDocRequest,
      );
    },
    onSuccess: (data) => {
      // Update travel doc cache with new data
      queryClient.setQueryData(accountKeys.travelDoc, data);
      // Invalidate travel doc query to ensure fresh data
      queryClient.invalidateQueries({ queryKey: accountKeys.travelDoc });
    },
    onError: (error) => {
      // Force logout scenarios are handled automatically by useApiMutation
      console.error('Error updating travel doc:', describeError(error));
    },
  });
};

export const useLoginByCredentials = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const recaptchaManager = useRecaptchaManager();
  const { logError } = useLogger();

  return useMutation({
    mutationFn: async ({
      username,
      password,
      restoreAccount,
    }: {
      username: string;
      password: string;
      restoreAccount?: boolean;
    }) => {
      // Since this is a manuale trigger, we can use ensureQueryData, also api key cache is 1 hour, so the chance of duplicate api call is low
      const clientKey = await queryClient.ensureQueryData(
        apiKeyQueryOptions(api, logError),
      );

      if (!clientKey) throw new Error('Missing API keys');

      return api.getAccessTokenByCredentials(
        { username, password, ...(restoreAccount ? { restoreAccount } : {}) },
        clientKey.id || '',
        clientKey.apiKey || '',
        await recaptchaManager.execute(RECAPTCHA_ACTIONS.LOGIN),
      );
    },
    onSuccess: async (data) => {
      await handleSuccessfulLogin(queryClient, data);
    },
    onError: async (error) => {
      await handleLoginError(queryClient, error, 'Credentials');
    },
  });
};

const AA_TOKENS_COOKIE_NAME = 'aa_tokens';
const AA_TOKENS_COOKIE_EXPIRY_DAYS = 180;

/**
 * Hook for logging out the current user locally.
 *
 * - Clears access token, refresh token, and user ID from AsyncStorage.
 * - Invalidates React Query caches for authentication-related queries.
 * - Does not call remote APIs; this is a local-only logout.
 */
export const useLocalLogout = () => {
  const appConfig = useAppConfig();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await AsyncStorage.multiRemove([
        accountKeys.accessToken.slice(1).join('_'),
        accountKeys.refreshToken.join('_'),
        accountKeys.userId.slice(1).join('_'),
        accountKeys.user.slice(1).join('_'),
        accountKeys.isGuestLogin.join('_'),
        accountKeys.twoFaLoginToken[0],
        'user_id',
        accountStorageKeys.combineAccountsContinueTapped,
        accountStorageKeys.combineAccountsRemindLaterTapped,
      ]);

      // Clear the aa_tokens cookie set for the host page on v5 SSO login.
      removeCookie(AA_TOKENS_COOKIE_NAME);
    },
    onSuccess: async () => {
      appConfig.clearPersistQueryClient?.();

      // manully seting setQueriesData to null, so that component get notified
      // simply doing removeQueries, reset querry without notifying component
      queryClient.setQueriesData(
        {
          predicate: (query) => query.queryKey.includes('sso'),
        },
        null,
      );

      // now remove whole querry, erro,state everything
      queryClient.removeQueries({
        predicate: (query) => query.queryKey.includes('sso'),
      });
      queryClient.removeQueries({ queryKey: ['avalon'], exact: false });
    },
  });
};

export const useLocalLogin = () => {
  const queryClient = useQueryClient();
  const onLoginSuccess = (data: AccessTokenResponse) => {
    // hotels-web, flights-web and the flights package read only these three;
    // the rest of the response is PII with no reason to ride on every request.
    const { accessToken, refreshToken, userId } = data;
    setCookie(
      AA_TOKENS_COOKIE_NAME,
      encodeURIComponent(JSON.stringify({ accessToken, refreshToken, userId })),
      AA_TOKENS_COOKIE_EXPIRY_DAYS,
      // Every deployed environment is HTTPS; local dev serves plain HTTP, where
      // a Secure cookie is dropped outright.
      typeof window !== 'undefined' && window.location.protocol === 'https:',
    );
    handleSuccessfulLogin(queryClient, data);
  };
  const onLoginError = (error: Error) => {
    handleLoginError(queryClient, error, 'sso_widget');
  };
  const onGuestLogin = () => {
    handleGuestLogin(queryClient, true);
  };
  return {
    onLoginSuccess,
    onLoginError,
    onGuestLogin,
  };
};

/**
 * Hook for logging in with mobile number.
 *
 * - Verifies that no user is already logged in.
 * - Fetches client API key (cached for 59 minutes).
 * - Calls SSO `/auth/v2/authorization/by-mobile-otp` API with recaptcha.
 * - On success:
 *   - Returns verification token that can be used for OTP verification.
 *   - Triggers OTP sending to the mobile number.
 * - On error:
 *   - Logs error to console.
 *
 * @returns {UseMutationResult<MobileLoginResponse>} React Query mutation for mobile login.
 */
export const useLoginByMobile = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const recaptchaManager = useRecaptchaManager();
  const { logError } = useLogger();

  return useMutation({
    mutationFn: async ({
      dialingCode,
      mobileNumber,
      restoreAccount,
    }: {
      dialingCode: string;
      mobileNumber: string;
      restoreAccount?: boolean;
    }) => {
      const clientKey = await queryClient.ensureQueryData(
        apiKeyQueryOptions(api, logError),
      );

      if (!clientKey) throw new Error('Missing API keys');

      // Concatenate dialing code and mobile number for the API call
      const mobilePhone = `${dialingCode}${mobileNumber}`;

      return api.loginByMobile(
        clientKey.apiKey || '',
        clientKey.id || '',
        { mobilePhone, ...(restoreAccount ? { restoreAccount } : {}) },
        await recaptchaManager.execute(
          RECAPTCHA_ACTIONS.LOGIN_MOBILE_PHONE_OTP,
        ),
      );
    },
    onSuccess: async (data) => {
      if (data.verificationToken) {
        await AsyncStorage.setItem(
          accountKeys.mobileVerificationToken[0],
          data.verificationToken,
        );
        queryClient.setQueryData(
          accountKeys.mobileVerificationToken,
          data.verificationToken,
        );
      }
    },
    onError: async () => {
      await AsyncStorage.removeItem(accountKeys.mobileVerificationToken[0]);
      queryClient.setQueryData(accountKeys.mobileVerificationToken, null);
    },
  });
};

/**
 * Hook for logging in with Google authentication.
 *
 * - Verifies that no user is already logged in.
 * - Fetches client API key (cached for 59 minutes).
 * - Calls SSO `/auth/v2/authorization/mobile/google` API with Google token.
 * - On success:
 *   - Persists refresh/access tokens and user ID in AsyncStorage.
 *   - Updates React Query cache.
 *   - Invalidates dependent queries (refreshToken, userId, user).
 * - On error:
 *   - Logs error to console.
 *
 * @returns {UseMutationResult<AccessTokenResponse>} React Query mutation for Google login.
 */
export const useLoginByGoogle = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      googleAuthToken,
      googleClientId,
      optIn2FA = false,
      status,
      restoreAccount,
    }: {
      googleAuthToken: string;
      googleClientId: string;
      optIn2FA?: boolean;
      status?: string;
      restoreAccount?: boolean;
    }) => {
      // Check if user is already logged in at mutation time
      const refreshToken = await AsyncStorage.getItem(
        accountKeys.refreshToken.join('_'),
      );
      if (refreshToken) {
        throw new Error('User already logged in');
      }

      const clientKey = await ensureSocialAuthClientKey(queryClient, api);

      if (!clientKey?.id || !clientKey?.apiKey) {
        throw new Error('Missing API keys');
      }

      const { status: httpStatus, data } =
        await api.getAccessTokenByGoogleAuthentication(
          clientKey.id,
          clientKey.apiKey,
          undefined, // acceptLanguage
          {
            googleAuthToken,
            googleClientId,
            optIn2FA,
            status: status ?? null,
            ...(restoreAccount ? { restoreAccount } : {}),
          },
        );

      const interpreted = interpretGoogleAuthExchange(httpStatus, data);
      if (interpreted.kind === 'failed') {
        throw new Error('Google authentication failed');
      }
      if (interpreted.kind === 'authenticated') {
        await handleSuccessfulLogin(queryClient, interpreted.data);
        return interpreted.data;
      }
      return interpreted.data;
    },
    onError: async (error) => {
      await handleLoginError(queryClient, error, 'Google');
    },
  });
};

/**
 * Hook for logging in with Facebook authentication.
 *
 * - Verifies that no user is already logged in.
 * - Fetches client API key (cached for 59 minutes).
 * - Calls SSO `/auth/v2/authorization/mobile/facebook` API with Facebook token.
 * - On success:
 *   - Persists refresh/access tokens and user ID in AsyncStorage.
 *   - Updates React Query cache.
 *   - Invalidates dependent queries (refreshToken, userId, user).
 * - On error:
 *   - Logs error to console.
 *
 * @returns {UseMutationResult<AccessTokenResponse>} React Query mutation for Facebook login.
 */
export const useLoginByFacebook = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const { logError } = useLogger();

  return useMutation({
    mutationFn: async ({
      facebookAuthToken,
      optIn2FA = false,
      status,
      restoreAccount,
    }: {
      facebookAuthToken: string;
      optIn2FA?: boolean;
      status?: string;
      restoreAccount?: boolean;
    }) => {
      // Check if user is already logged in at mutation time
      const refreshToken = await AsyncStorage.getItem(
        accountKeys.refreshToken.join('_'),
      );
      if (refreshToken) {
        throw new Error('User already logged in');
      }
      const clientKey = await queryClient.ensureQueryData(
        apiKeyQueryOptions(api, logError),
      );

      if (!clientKey) throw new Error('Missing API keys');

      return api.getAccessTokenByFacebookAuthentication(
        clientKey.id || '',
        clientKey.apiKey || '',
        'en-gb',
        {
          facebookAuthToken,
          optIn2FA,
          status,
          ...(restoreAccount ? { restoreAccount } : {}),
        },
      );
    },
    onSuccess: async (data) => {
      await handleSuccessfulLogin(queryClient, data);
    },
    onError: async (error) => {
      await handleLoginError(queryClient, error, 'Facebook');
    },
  });
};

/**
 * Hook for logging in with Apple authentication.
 *
 * - Verifies that no user is already logged in.
 * - Fetches client API key (cached for 59 minutes).
 * - Calls SSO `/auth/v2/authorization/mobile/apple` API with Apple auth data.
 * - On success:
 *   - Persists refresh/access tokens and user ID in AsyncStorage.
 *   - Updates React Query cache.
 *   - Invalidates dependent queries (refreshToken, userId, user).
 * - On error:
 *   - Logs error to console.
 *
 * @returns {UseMutationResult<AccessTokenResponse>} React Query mutation for Apple login.
 */
export const useSSOAppleLogin = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      appleId,
      authCode,
      email,
      firstName,
      lastName,
      restoreAccount,
    }: {
      appleId: string;
      authCode: string;
      email?: string;
      firstName?: string;
      lastName?: string;
      restoreAccount?: boolean;
    }) => {
      // Check if user is already logged in at mutation time
      const refreshToken = await AsyncStorage.getItem(
        accountKeys.refreshToken.join('_'),
      );
      if (refreshToken) {
        throw new Error('User already logged in');
      }

      const clientKey = await ensureSocialAuthClientKey(queryClient, api);

      if (!clientKey?.id || !clientKey?.apiKey) {
        throw new Error('Missing API keys');
      }

      const data = await api.getAccessTokenByAppleAuthentication(
        clientKey.id || '',
        clientKey.apiKey || '',
        undefined, // acceptLanguage
        {
          appleId,
          authCode,
          email,
          firstName,
          lastName,
          ...(restoreAccount ? { restoreAccount } : {}),
        },
      );

      if (!data.refreshToken) {
        throw new Error('Apple authentication failed');
      }

      return data;
    },
    onSuccess: async (data) => {
      await handleSuccessfulLogin(queryClient, data);
    },
    onError: async (error) => {
      await handleLoginError(queryClient, error, 'Apple');
    },
  });
};

/**
 * Handles successful login by persisting authentication data and updating React Query cache.
 *
 * - Stores refresh token, access token, and user ID in AsyncStorage.
 * - Removes legacy user_id from AsyncStorage.
 * - Updates React Query cache with access token.
 * - Invalidates dependent queries to trigger refetch of user data.
 *
 * @param queryClient React Query client instance.
 * @param data Login response containing tokens and user ID.
 */
export const handleSuccessfulLogin = async (
  queryClient: QueryClient,
  data: AccessTokenResponse,
) => {
  const accessToken = data.accessToken || '';
  const refreshToken = data.refreshToken || '';
  const userId = data.userId || getUserIdFromToken(refreshToken) || '';

  await AsyncStorage.setItem(accountKeys.refreshToken.join('_'), refreshToken);
  await AsyncStorage.setItem(
    accountKeys.accessToken.slice(1).join('_'),
    accessToken,
  );
  await AsyncStorage.multiRemove([
    'user_id', // remove legacy id
  ]);
  await AsyncStorage.setItem(accountKeys.userId.slice(1).join('_'), userId);

  // Keep React Query in sync immediately so follow-up calls (e.g. incomplete
  // signup phone-verification-channels) don't reuse a stale access token from
  // a previous guest/session while userId already reflects the new login.
  queryClient.setQueryData(accountKeys.refreshToken, refreshToken);
  queryClient.setQueryData(accountKeys.accessToken, accessToken);
  queryClient.setQueryData(accountKeys.userId, userId);
  queryClient.removeQueries({ queryKey: accountKeys.idToken });

  // refetch user info
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: accountKeys.accessToken }),
    queryClient.invalidateQueries({ queryKey: accountKeys.refreshToken }),
    queryClient.invalidateQueries({ queryKey: accountKeys.userId }),
    queryClient.invalidateQueries({ queryKey: accountKeys.user }),
    queryClient.invalidateQueries({ queryKey: accountKeys.creditBalance }),
  ]);

  await handleGuestLogin(queryClient, false);
};

/**
 * Handles login errors by logging the error and ensuring clean state.
 *
 * - Logs the error to console.
 * - Invalidates queries to ensure clean state on error.
 *
 * @param queryClient React Query client instance.
 * @param error The error that occurred during login.
 * @param loginType Type of login for better error logging context.
 */
export const handleLoginError = async (
  queryClient: QueryClient,
  error: unknown,
  loginType: string,
) => {
  console.error(`${loginType} login error:`, describeError(error));
  await handleApiError(error, undefined, queryClient);
  // Invalidate queries to ensure clean state on error
  queryClient.invalidateQueries({ queryKey: accountKeys.refreshToken });
  queryClient.invalidateQueries({ queryKey: accountKeys.userId });
  queryClient.invalidateQueries({ queryKey: accountKeys.user });
  queryClient.invalidateQueries({ queryKey: accountKeys.creditBalance });
};

export const useChangeUserName = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const apikeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken('userUser');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changeUserNameRequest: ChangeUserNameRequest) => {
      if (apikeyQuery.error) {
        console.error(
          'Error fetching API - ',
          caller,
          ':',
          describeError(apikeyQuery.error),
        );
      }

      if (accessTokenQuery.error) {
        console.error(
          'Error fetching access token - ',
          caller,
          ':',
          describeError(accessTokenQuery.error),
        );
      }

      const [apiKey, accessToken] = await Promise.all([
        apikeyQuery.getToken(),
        accessTokenQuery.getToken(),
      ]);

      if (!apiKey?.apiKey) throw new Error('Missing API keys');
      if (!apiKey?.id) throw new Error('Missing Client ID');
      if (!accessToken) throw new Error('Missing access tokens');
      if (!userIdQuery.data) throw new Error('Missing user ID');

      // Get mobileOtpAuthToken from AsyncStorage
      const mobileOtpAuthToken =
        (await AsyncStorage.getItem('otp_auth_token_mobile')) || '';
      if (!mobileOtpAuthToken) {
        throw new Error('Missing mobile OTP auth token');
      }

      return api.changeUserName(
        userIdQuery.data,
        apiKey.id,
        apiKey.apiKey || '',
        accessToken,
        mobileOtpAuthToken,
        changeUserNameRequest,
      );
    },
    onSuccess: async () => {
      // Invalidate user query to ensure fresh data
      queryClient.invalidateQueries({ queryKey: accountKeys.user });
    },
    onError: async (error) => {
      console.error('changeUserName error:', describeError(error));
    },
  });
};

/**
 * Hook for checking if a username exists.
 *
 * This hook provides a mutation function to check if a username already exists
 * in the system. It follows the same pattern as other API hooks in this file.
 *
 * @returns {UseMutationResult} React Query mutation for checking username existence.
 */
export const useCheckIfUserNameExists = () => {
  const api = useSsoApiService();
  const apikeyQuery = useApiKey();
  return useMutation({
    mutationFn: async ({
      username,
    }: {
      username: string;
    }): Promise<boolean> => {
      try {
        const clientKey = apikeyQuery.data;
        if (!clientKey) {
          throw new Error('Missing API keys');
        }
        await api.checkIfUserNameExists(
          username,
          clientKey.id || '',
          clientKey.apiKey || '',
        );
        // Call succeeded → username *exists*, so it's NOT available
        return false;
      } catch (error: any) {
        // 404 means user not found → username IS available
        if (error.response?.status === 404) {
          return true;
        }
        // Re-throw other errors for React Query to handle
        throw error;
      }
    },
  });
};

/**
 * Hook for user signup.
 *
 * This hook provides a mutation function to create a new user account.
 * It follows the same pattern as other API hooks in this file.
 *
 * @returns {UseMutationResult} React Query mutation for user signup.
 */
export const useSignUp = () => {
  const api = useSsoApiService();
  const apikeyQuery = useApiKey();
  const recaptchaManager = useRecaptchaManager();

  return useMutation({
    mutationFn: async (signUpData: {
      username: string;
      password: string;
      dateOfBirth: string;
      firstName: string;
      lastName: string;
      gender: string;
      dialingCode: string;
      mobilePhone: string;
      sendEmail?: boolean;
    }) => {
      const clientKey = apikeyQuery.data;
      if (!clientKey) throw new Error('Missing API keys');
      const result = await api.signUp(
        clientKey.apiKey || '',
        clientKey.id || '',
        await recaptchaManager.execute(RECAPTCHA_ACTIONS.SIGNUP), // recaptchaToken
        undefined, // xGaClientId
        {
          username: signUpData.username,
          password: signUpData.password,
          dateOfBirth: signUpData.dateOfBirth,
          firstName: signUpData.firstName,
          lastName: signUpData.lastName,
          gender: signUpData.gender,
          dialingCode: signUpData.dialingCode,
          mobilePhone: signUpData.mobilePhone,
          sendEmail: signUpData.sendEmail ?? true,
        },
      );

      return result;
    },
    onError: async (error: any) => {
      // Log only sanitized, non-sensitive information to avoid exposing PII
      console.error('signUp error:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        requestUrl: error.config?.url,
        errorCode: error.response?.data?.code,
      });
    },
  });
};

export const useSendForgotPasswordEmail = () => {
  const api = useSsoApiService();
  const apikeyQuery = useApiKey();

  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      if (apikeyQuery.error) {
        console.error(
          'Error fetching API - ',
          describeError(apikeyQuery.error),
        );
      }

      const clientKey = apikeyQuery.data;
      if (!clientKey) throw new Error('Missing API keys');

      const requestBody = { email }; // pass email in request body

      const result = await api.forgotPassword(
        clientKey.apiKey || '', // apiKey
        clientKey.id || '', // accessToken or clientId? depends on your implementation
        clientKey.id || '', // clientId
        undefined, // xGaClientId (optional)
        requestBody, // forgotPasswordRequestBody
      );

      return result;
    },
    onError: (error) => {
      console.error(
        'Failed to send forgot password email',
        describeError(error),
      );
    },
  });
};

/**
 * Hook that retrieves user detail change configuration.
 *
 * - Requires that user ID, API key, and access token are all available.
 * - Disabled until all prerequisites are satisfied.
 * - Always refetches in the background on mount; cached data is shown instantly while fresh data loads.
 */
export const useUserDetailChangeConfiguration = (_caller: string) => {
  void _caller;
  const api = useSsoApiService();

  return useQuery({
    queryKey: accountKeys.userDetailChangeConfiguration,
    staleTime: 0, // always stale: serve cache instantly, refetch in background on every mount
    queryFn: async () => {
      return await api.userDetailChangeConfiguration();
    },
  });
};

/**
 * Query that appends native SSO auth params to a URL for in-app WebViews.
 */
export const useAppendExternalAuth = (
  requestUrl?: string,
  nearbyLocation?: NearbyLocation | null,
) => {
  const queryClient = useQueryClient();
  const accessTokenQuery = useAccessToken('useAppendExternalAuth');
  const isLoggedInQuery = useIsLoggedIn();
  const url = requestUrl ?? '';

  return useQuery<string>({
    queryKey: accountKeys.externalAuthUrl(url),
    queryFn: async () => {
      // Skip token resolution entirely for untrusted hosts — appendExternalAuth
      if (!isTrustedAuthHost(url)) {
        return url;
      }

      let tokens: ExternalAuthTokens = {};
      if (isLoggedInQuery.isLoggedIn) {
        try {
          // Read at call time: useUserId is still pending on the render that enables this query.
          const accessToken = await accessTokenQuery.getToken();
          const refreshToken = await queryClient.ensureQueryData(
            refreshTokenQueryOptions(),
          );
          tokens = {
            accessToken,
            refreshToken,
            userId: getUserIdFromToken(refreshToken),
          };
        } catch (error) {
          console.error(
            'useAppendExternalAuth: failed to resolve tokens -',
            error,
          );
        }
      }

      return appendExternalAuth(url, tokens, nearbyLocation);
    },
    enabled: !!url && isLoggedInQuery.isFetched,
  });
};

export { handleGuestLogin, useGuestLogin } from './useGuestLogin';
